import { useState } from 'react'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestJson } from '@/lib/http'

type IssueRecord = {
  id: string
  status: string
  severity: string
  issue_type: string
  summary: string
  details?: string
}

type Props = {
  issues: IssueRecord[]
}

export default function PlatformOpsIssuesIndex({ issues }: Props) {
  const [notesByIssue, setNotesByIssue] = useState<Record<string, string>>({})
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const changeStatus = async (issueId: string, status: 'open' | 'acknowledged' | 'resolved') => {
    setMessage('')
    setError('')

    try {
      await requestJson(`/platform/business-registration/us/ops_issues/${issueId}`, {
        method: 'PATCH',
        data: {
          status,
          notes: notesByIssue[issueId],
        },
      })
      setMessage(`Issue marked ${status}`)
      window.location.reload()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed')
    }
  }

  return (
    <PublicLayout title="Ops Issues" fullWidth>
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <h1 className="text-2xl font-semibold">Operations Issues</h1>
        {message && <p className="text-sm text-green-700">{message}</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}

        {issues.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">No operations issues.</CardContent>
          </Card>
        ) : (
          issues.map((issue) => (
            <Card key={issue.id}>
              <CardHeader>
                <CardTitle className="text-base">[{issue.severity}] {issue.issue_type} • {issue.status}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">{issue.summary}</p>
                {issue.details && <p className="text-xs text-muted-foreground">{issue.details}</p>}
                <Input
                  placeholder="Resolution notes"
                  value={notesByIssue[issue.id] || ''}
                  onChange={(event) => setNotesByIssue({ ...notesByIssue, [issue.id]: event.target.value })}
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => changeStatus(issue.id, 'acknowledged')}>Acknowledge</Button>
                  <Button onClick={() => changeStatus(issue.id, 'resolved')}>Resolve</Button>
                  <Button variant="outline" onClick={() => changeStatus(issue.id, 'open')}>Reopen</Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </PublicLayout>
  )
}
